'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { cn, EASE_PREMIUM as EASE } from '@sharpr/ui';

export interface FaqItem {
  slug: string;
  q: string;
  a: ReactNode;
}

export function FaqAccordion({ items }: { items: FaqItem[] }) {
  const [openSlug, setOpenSlug] = useState<string | null>(null);
  const prefersReducedMotion = useReducedMotion();
  const expandTransition = prefersReducedMotion
    ? { duration: 0 }
    : { duration: 0.22, ease: EASE };

  // Open + scroll to a question when the URL hash matches one of the slugs.
  // Runs on mount (deep-link from email/support) and on hashchange (back/forward).
  useEffect(() => {
    function syncFromHash() {
      const hash = window.location.hash.replace(/^#/, '');
      if (!hash) return;
      if (items.some((item) => item.slug === hash)) {
        setOpenSlug(hash);
        requestAnimationFrame(() => {
          document
            .getElementById(hash)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Move keyboard focus to the question so a screen-reader user
          // following a deep link hears the question announced. preventScroll
          // avoids fighting the scrollIntoView call above.
          document
            .getElementById(`faq-button-${hash}`)
            ?.focus({ preventScroll: true });
        });
      }
    }
    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, [items]);

  function toggle(slug: string) {
    const next = openSlug === slug ? null : slug;
    setOpenSlug(next);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.hash = next ?? '';
      window.history.replaceState(null, '', url.toString());
    }
    // When expanding, nudge the question into view if the answer would render
    // below the fold. block:'nearest' is a no-op when already fully visible.
    // The delay matches the panel's expand animation so the scroll target
    // accounts for the answer's final height.
    if (next && typeof window !== 'undefined') {
      const delay = prefersReducedMotion ? 0 : 240;
      window.setTimeout(() => {
        document
          .getElementById(next)
          ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }, delay);
    }
  }

  return (
    <div className="rounded-2xl border border-border dark:border-border-primary bg-white dark:bg-bg-secondary shadow-card overflow-hidden">
      {items.map((item, idx) => {
        const isOpen = openSlug === item.slug;
        const panelId = `faq-panel-${item.slug}`;
        const buttonId = `faq-button-${item.slug}`;
        const isLast = idx === items.length - 1;
        return (
          <div
            key={item.slug}
            id={item.slug}
            className={cn(
              'scroll-mt-24',
              !isLast && 'border-b border-border dark:border-border-primary',
            )}
          >
            <button
              id={buttonId}
              type="button"
              onClick={() => toggle(item.slug)}
              aria-expanded={isOpen}
              aria-controls={panelId}
              className={cn(
                'w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-4 text-left',
                'text-sm sm:text-base font-medium text-fg dark:text-gray-100',
                'hover:bg-muted/40 dark:hover:bg-bg-tertiary/40',
                'focus-visible:outline-none focus-visible:bg-muted/40 dark:focus-visible:bg-bg-tertiary/40',
                'transition-colors duration-150',
              )}
            >
              <span className="flex-1">{item.q}</span>
              <ChevronDown
                aria-hidden
                className={cn(
                  'w-4 h-4 shrink-0 text-fg/50 dark:text-gray-400 transition-transform duration-200',
                  isOpen && 'rotate-180',
                )}
              />
            </button>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  key="content"
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={expandTransition}
                  className="overflow-hidden"
                >
                  <div className="px-5 sm:px-6 pb-5 pt-1 text-sm sm:text-[15px] text-fg/75 dark:text-gray-300 leading-relaxed">
                    {item.a}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}
